import type { FC, FormEvent, ReactNode } from 'react'
import type { ListingDetail } from '@/types'

import { useEffect, useState } from 'react'
import { LoaderCircle, Users, Video } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatPrice } from '@/lib/format'
import { submitLead } from '@/lib/submit-lead'
import { isForSale } from '@/components/homes/listing-card'

import {
    FormError,
    FormSuccess,
    HoneypotField,
    TextAreaField,
    TextField
} from '@/components/generals/form-fields'

interface TourRequestCardProps {
    listing: ListingDetail
    className?: string
}

type DayOption = { value: string, weekday: string, day: string, month: string }

const TIMES = ['Morning', 'Afternoon', 'Evening']

const toDateValue = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${date.getFullYear()}-${month}-${day}`
}

export const TourRequestCard: FC<TourRequestCardProps> = ({ listing, className }): ReactNode => {
    const [mode, setMode] = useState<'tour' | 'question'>('tour')
    const [days, setDays] = useState<DayOption[]>([])
    const [date, setDate] = useState('')
    const [time, setTime] = useState('Morning')
    const [kind, setKind] = useState('In person')
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [message, setMessage] = useState('')
    const [website, setWebsite] = useState('')
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle')
    const [error, setError] = useState<string | null>(null)

    // Dates depend on the visitor's own calendar, so they are built after the page loads.
    useEffect(() => {
        const options: DayOption[] = Array.from({ length: 7 }, (_, offset) => {
            const day = new Date()
            day.setDate(day.getDate() + offset + 1)

            return {
                value: toDateValue(day),
                weekday: day.toLocaleDateString('en-US', { weekday: 'short' }),
                day: day.toLocaleDateString('en-US', { day: 'numeric' }),
                month: day.toLocaleDateString('en-US', { month: 'short' })
            }
        })

        setDays(options)
        setDate(options[0].value)
    }, [])

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setError(null)
        setStatus('sending')

        const result = await submitLead({
            type: mode,
            name,
            email,
            phone,
            message,
            listingId: listing.id,
            website,
            ...(mode === 'tour' ? { tourDate: date, tourTime: time, tourKind: kind } : {})
        })

        if (result.ok) {
            setStatus('sent')

            return
        }

        setStatus('idle')
        setError(result.message)
    }

    const chosenDay = days.find(day => day.value === date)

    return (
        <div id='tour' className={cn('scroll-mt-28 rounded-2xl border border-border bg-white p-6 shadow-float', className)}>
            <div className='flex flex-wrap items-baseline justify-between gap-2'>
                <p className='text-2xl font-bold tracking-tight'>{formatPrice(listing.price)}</p>
                <span className={cn(
                    'rounded-full px-3 py-1 text-xs font-semibold',
                    isForSale(listing.status) ? 'bg-brand-soft text-primary' : 'bg-foreground text-white'
                )}>
                    <span data-mdlfy-cms={`listings:${listing.id}:status`}>{listing.status}</span>
                </span>
            </div>

            {status === 'sent'
                ? (
                    <FormSuccess
                        title={mode === 'tour' ? 'Tour requested' : 'Question sent'}
                        onReset={() => {
                            setStatus('idle')
                            setMessage('')
                        }}
                        resetLabel='Send another request'
                    >
                        {mode === 'tour'
                            ? <>We'll confirm your {kind === 'Video tour' ? 'video tour' : 'visit'}{chosenDay ? ` on ${chosenDay.weekday}, ${chosenDay.month} ${chosenDay.day}` : ''} by email within one business day.</>
                            : <>Thanks for asking. The listing agent will reply to {email} within one business day.</>}
                    </FormSuccess>
                )
                : (
                    <form onSubmit={handleSubmit} className='relative mt-5 space-y-5' noValidate={false}>
                        <div className='grid grid-cols-2 rounded-xl bg-muted p-1' role='radiogroup' aria-label='What would you like to do?'>
                            {(['tour', 'question'] as const).map(option => (
                                <button
                                    key={option}
                                    type='button'
                                    role='radio'
                                    aria-checked={mode === option}
                                    onClick={() => setMode(option)}
                                    className={cn('h-10 rounded-lg text-sm font-semibold transition', mode === option ? 'bg-white shadow-sm' : 'text-muted-foreground hover:text-foreground')}
                                >
                                    {option === 'tour' ? 'Schedule a tour' : 'Ask a question'}
                                </button>
                            ))}
                        </div>

                        {mode === 'tour' && (
                            <>
                                <fieldset>
                                    <legend className='mb-2 text-sm font-medium'>Pick a day</legend>
                                    <div className='no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 pb-1'>
                                        {days.length === 0 && Array.from({ length: 4 }, (_, index) => (
                                            <span key={index} className='h-20 w-16 shrink-0 animate-pulse rounded-xl bg-muted' />
                                        ))}

                                        {days.map(day => (
                                            <button
                                                key={day.value}
                                                type='button'
                                                aria-pressed={date === day.value}
                                                onClick={() => setDate(day.value)}
                                                className={cn(
                                                    'flex h-20 w-16 shrink-0 flex-col items-center justify-center rounded-xl border text-center transition',
                                                    date === day.value ? 'border-foreground bg-foreground text-background' : 'border-border hover:border-foreground'
                                                )}
                                            >
                                                <span className='text-xs'>{day.weekday}</span>
                                                <span className='text-xl font-bold'>{day.day}</span>
                                                <span className='text-xs'>{day.month}</span>
                                            </button>
                                        ))}
                                    </div>
                                </fieldset>

                                <fieldset>
                                    <legend className='mb-2 text-sm font-medium'>Time of day</legend>
                                    <div className='grid grid-cols-3 gap-2'>
                                        {TIMES.map(option => (
                                            <button
                                                key={option}
                                                type='button'
                                                aria-pressed={time === option}
                                                onClick={() => setTime(option)}
                                                className={cn('h-10 rounded-xl border text-sm font-medium transition', time === option ? 'border-foreground ring-1 ring-foreground' : 'border-border hover:border-foreground')}
                                            >
                                                {option}
                                            </button>
                                        ))}
                                    </div>
                                </fieldset>

                                <fieldset>
                                    <legend className='mb-2 text-sm font-medium'>Tour type</legend>
                                    <div className='grid grid-cols-2 gap-2'>
                                        {[{ value: 'In person', icon: Users }, { value: 'Video tour', icon: Video }].map(option => (
                                            <button
                                                key={option.value}
                                                type='button'
                                                aria-pressed={kind === option.value}
                                                onClick={() => setKind(option.value)}
                                                className={cn('flex h-11 items-center justify-center gap-2 rounded-xl border text-sm font-medium transition', kind === option.value ? 'border-foreground ring-1 ring-foreground' : 'border-border hover:border-foreground')}
                                            >
                                                <option.icon className='size-4' aria-hidden='true' />
                                                {option.value}
                                            </button>
                                        ))}
                                    </div>
                                </fieldset>
                            </>
                        )}

                        <TextField label='Your name' value={name} onChange={event => setName(event.target.value)} required autoComplete='name' maxLength={100} />
                        <TextField label='Email' type='email' value={email} onChange={event => setEmail(event.target.value)} required autoComplete='email' maxLength={254} />
                        <TextField label='Phone' type='tel' optional value={phone} onChange={event => setPhone(event.target.value)} autoComplete='tel' maxLength={40} />
                        <TextAreaField
                            label={mode === 'tour' ? 'Anything we should know?' : 'Your question'}
                            optional={mode === 'tour'}
                            required={mode === 'question'}
                            rows={3}
                            value={message}
                            onChange={event => setMessage(event.target.value)}
                            maxLength={2000}
                            placeholder={mode === 'tour' ? 'Pre-approved, bringing family, flexible on time...' : 'Ask about HOA fees, schools, offers...'}
                        />

                        <HoneypotField value={website} onChange={setWebsite} />
                        <FormError message={error} />

                        <button
                            type='submit'
                            disabled={status === 'sending' || (mode === 'tour' && !date)}
                            className='bg-brand-gradient flex h-12 w-full items-center justify-center gap-2 rounded-xl text-base font-semibold text-white transition hover:brightness-105 disabled:opacity-70'
                        >
                            {status === 'sending' && <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />}
                            {status === 'sending' ? 'Sending...' : mode === 'tour' ? 'Request a tour' : 'Send question'}
                        </button>

                        <p className='text-center text-xs text-muted-foreground'>Free and no obligation. We reply within one business day.</p>
                    </form>
                )}
        </div>
    )
}
